import { cn } from "@/lib/cn";

// Named colours for the plain informational badge, so callers say what a badge
// MEANS instead of pasting the same eight Tailwind classes at every use site.
// `className` still wins, for the one-offs a name would only obscure.
const TONES = {
  slate: "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300",
  brand: "bg-brand-500/10 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300",
  emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  amber: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  rose: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  sky: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
};

export function Badge({ className, tone, children }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold",
        tone && TONES[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

// Run + batch-item status. Colors are shared everywhere, so every status a
// batch item can hold gets its own hue — several of these used to fall through
// to the RUNNING blue and read as "still running" when they weren't.
const STATUS_STYLES = {
  // terminal — good
  PASSED:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  COMPLETED:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  // terminal — bad
  FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  ERROR: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  ERRORED: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  ORPHANED: "bg-rose-200 text-rose-900 dark:bg-rose-900/50 dark:text-rose-200",
  // terminal — neutral / inconclusive
  INCONCLUSIVE:
    "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-300",
  CANCELLED: "bg-slate-200 text-slate-600 dark:bg-slate-500/25 dark:text-slate-300",
  STOPPED: "bg-slate-200 text-slate-600 dark:bg-white/10 dark:text-slate-300",
  // interrupted = a run that was killed mid-flight (crash / restart / --reload),
  // distinct from a user Stop (CANCELLED) and a real engine ERROR.
  INTERRUPTED: "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
  // blocked = manual-only, deliberately never executed. A first-class settled
  // status (see BatchReportTable's SETTLED list) that fell through to the PENDING
  // grey, making "will never run" look identical to "cancelled" and "stopped".
  BLOCKED: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
  // in-flight
  CLAIMED:
    "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300",
  RUNNING:
    "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
  PAUSED: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  // waiting
  QUEUED: "bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-400",
  REQUEUED: "bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-400",
  PENDING: "bg-slate-200 text-slate-600 dark:bg-white/10 dark:text-slate-300",
};

export function StatusBadge({ status, className }) {
  const key = String(status || "").toUpperCase();
  const pulse = key === "RUNNING" || key === "CLAIMED";
  return (
    // className is forwarded so a caller can stop the pill being squeezed in a
    // tight flex row (shrink-0); without it the prop was silently dropped.
    <Badge className={cn(STATUS_STYLES[key] || STATUS_STYLES.PENDING, className)}>
      <span
        className={cn("h-1.5 w-1.5 rounded-full bg-current", pulse && "animate-pulse")}
      />
      {key || "PENDING"}
    </Badge>
  );
}

// Bug severity — low / medium / high / critical.
const SEVERITY_STYLES = {
  low: "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300",
  medium: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
  critical: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
};

export function SeverityTag({ severity }) {
  const key = String(severity || "low").toLowerCase();
  return (
    <Badge className={cn("uppercase", SEVERITY_STYLES[key] || SEVERITY_STYLES.low)}>
      {key}
    </Badge>
  );
}
